import React, { useState } from 'react';
import { supabaseClient } from '../../../shared/utils/supabaseClient';
import { MESSAGING_CONSENT_VERSION } from '../../onboarding/constants/policyVersions';
import MessagingConsentText from '../../onboarding/components/MessagingConsentText';

// One-time messaging re-consent for EXISTING members (Stage 45 / piece 3). Shown on
// dashboard load when the member has no member_consents row with the current
// messaging_consent_version. Records via record_consent_self (reuses their existing
// signature + agreement version/hash from myConsent + the messaging params) —
// method='reconsent'. Dismissible ("Maybe later") — re-prompts next login until accepted;
// WhatsApp (piece 5's gate) simply won't reach them until they accept.
//
// The v2 body is rendered verbatim via the shared MessagingConsentText; only the wrapper
// framing is tuned for someone already in a circle. Framing copy finalized (Frannie).
const ReconsentModal = ({ memberData, myConsent, onAccepted, onDismiss }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleAccept = async () => {
    // Need the member's existing agreement fields to append a valid consent row.
    if (!myConsent?.terms_version || !myConsent?.terms_sha256 || !myConsent?.signature) {
      setError('We could not confirm your existing agreement. Please contact your group admin.');
      return;
    }
    setSaving(true); setError(null);
    try {
      const { error: rpcErr } = await supabaseClient.rpc('record_consent_self', {
        p_group_id: memberData.group_id,
        p_member_db_id: memberData.id,
        p_signature: myConsent.signature,
        p_agreed: true,
        p_terms_version: myConsent.terms_version,
        p_terms_sha256: myConsent.terms_sha256,
        p_client_signed_at: new Date().toISOString(),
        p_messaging_accepted: true,
        p_messaging_version: MESSAGING_CONSENT_VERSION,
        p_messaging_method: 'reconsent',
      });
      if (rpcErr) throw rpcErr;
      onAccepted();
    } catch (e) {
      setError(e.message || 'Could not save. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-neutral-100 rounded-lg w-full max-w-md shadow-xl flex flex-col max-h-[90vh]">
        <div className="px-6 py-5 flex-1 overflow-y-auto">
          {/* Framing for existing members (Frannie). */}
          <h2 className="text-lg font-semibold text-neutral-900 mb-2">A quick note on how we'll reach you</h2>
          <p className="text-sm text-neutral-600 mb-3">
            You're already part of your circle. Here's how we keep you posted, now including WhatsApp
            updates. Please confirm:
          </p>
          {/* v2 canonical body — verbatim, shared source of truth. */}
          <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-3">
            <MessagingConsentText />
          </div>
          {error && <p className="mt-3 text-sm text-error-600">{error}</p>}
        </div>

        <div className="flex space-x-3 px-6 py-4 border-t border-neutral-200 flex-shrink-0">
          <button
            onClick={onDismiss}
            disabled={saving}
            className="flex-1 px-4 py-2 border border-neutral-300 rounded-lg text-neutral-700 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50"
          >
            Maybe later
          </button>
          <button
            onClick={handleAccept}
            disabled={saving}
            className="flex-1 px-4 py-2 bg-primary-600 text-neutral-50 rounded-lg text-sm font-semibold hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Sounds good'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReconsentModal;
